import { CrossCircleIcon, NavIcons } from "assets/icons";
import { If } from "components";
import { useBoolean } from "hooks";
import { t } from "i18next";
import { ModeratorRoutesPrivileges, useHasPrivilege } from "modules/common";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { Button, TableItemStatus } from "ui";

import {
  ExpeditionBadge,
  OrderUnpackingStatusLabel,
  PersonalOrderBadge
} from "../../../../components";
import {
  AVAILABLE_TO_CANCEL_ORDER_STATUSES,
  ORDER_STATUSES_LIST,
  OrderStatus
} from "../../../../constants";
import { CancelOrderModal } from "../../CancelOrderModal";
import { ForceCloseExpeditionModal } from "../../ForceCloseExpeditionModal";
import { OrderModalHeaderProps } from "../OrderModal.types";

import styles from "../OrderModal.module.scss";

export const OrderHeader = ({
  refetchList,
  refetchOrder,
  orderDetails
}: OrderModalHeaderProps) => {
  const {
    value: isCancelModalOpen,
    setTrue: openCancelModal,
    setFalse: closeCancelModal
  } = useBoolean();

  const {
    value: isForceCloseModalOpen,
    setTrue: openForceCloseModal,
    setFalse: closeForceCloseModal
  } = useBoolean();

  const orderStatusIndex = ORDER_STATUSES_LIST.findIndex(
    (status) => status.value === orderDetails?.status
  );

  const canForceCloseExpedition =
    orderDetails.isExpedition &&
    orderDetails.status === OrderStatus.Expedited;

  const navigate = useNavigate();

  const canSeeConversations = useHasPrivilege(
    ModeratorRoutesPrivileges.AiAssistants
  );

  return (
    <>
      <div className={styles.orderModal__header}>
        <div>
          <div className={styles.orderModal__header__statusWrapper}>
            <TableItemStatus
              activeStatusIndex={orderStatusIndex}
              statusesList={ORDER_STATUSES_LIST}
            />

            {orderDetails.isExpedition && <ExpeditionBadge />}

            {orderDetails.isPersonalOrder && <PersonalOrderBadge />}

            <OrderUnpackingStatusLabel
              unpackingStatus={orderDetails.unpackingStatus}
              quantityOfItemsToUnpack={orderDetails.quantityOfItemsToUnpack}
              unpackedAt={orderDetails.unpackedAt}
            />
          </div>
        </div>
        <div className={styles.orderModal__header__buttons}>
          <If
            condition={
              orderDetails?.status &&
              AVAILABLE_TO_CANCEL_ORDER_STATUSES.includes(orderDetails?.status)
            }
          >
            <Button
              type="button"
              title={t("Cancel Order")}
              icon={<CrossCircleIcon width={24} height={24} />}
              variant="default"
              onClick={openCancelModal}
            />
          </If>
          <If condition={canForceCloseExpedition}>
            <Button
              type="button"
              title={t("Force-close as delivered")}
              variant="default"
              onClick={openForceCloseModal}
            />
          </If>
          <If condition={canSeeConversations && !!orderDetails.conversationId}>
            <Button
              type="button"
              title={t("Open the conversation")}
              icon={<NavIcons.ConversationsIcon width={24} height={24} />}
              variant="default"
              onClick={() =>
                navigate(
                  `/ai/conversations?conversationId=${orderDetails.conversationId}`
                )
              }
            />
          </If>
        </div>

        <If condition={isCancelModalOpen}>
          <CancelOrderModal
            orderId={orderDetails.id}
            refetchList={refetchList}
            refetchDetails={refetchOrder}
            isOpen={isCancelModalOpen}
            onClose={closeCancelModal}
          />
        </If>
        <If condition={isForceCloseModalOpen}>
          <ForceCloseExpeditionModal
            orderId={orderDetails.id}
            refetchList={refetchList}
            refetchDetails={refetchOrder}
            isOpen={isForceCloseModalOpen}
            onClose={closeForceCloseModal}
          />
        </If>
      </div>
      {orderDetails.hasUnavailableItems && (
        <div className={styles.orderModal__header__unavailableItemsLabel}>
          {t("Order has unavailable items marked as unavailable at:")}{" "}
          {moment(orderDetails.markedAsUnavailableAt).format(
            "DD/MM/YYYY HH:mm"
          )}
        </div>
      )}
    </>
  );
};
